// src/hooks/useInternationalOnly.ts
//
// GEO GATE FOR THE INTERNATIONAL CAMPAIGN PAGES (/usa /uae /love /horoscope).
//
// These four were built as paid-traffic landing pages for foreign visitors, so
// the ad platform did the targeting and nothing in the app ever did. Then they
// were added to the sitemap and to IndexPolicy's INDEXABLE set, which is when
// Indian visitors started arriving on them from organic search. /usa sells "an
// Indian astrologer, from the USA" and /uae prices in AED, so an Indian visitor
// lands on a page that is wrong for them in both pitch and currency.
//
// Same shape as useGuestChat, and for the same reason: `userCountry` DEFAULTS to
// 'IN', so "the probe has not answered yet" and "the probe said India" are
// indistinguishable until `geoChecked` flips. A gate that reads userCountry
// without waiting would bounce every USA visitor home on first paint, which is
// exactly the traffic these pages are bought for.

import { useAuth } from "../context/AuthContext";
import { isPrerender } from "../utils/isPrerender";

export function useInternationalOnly(): {
  allowed: boolean;
  resolvingGeo: boolean;
} {
  const { user, currency, userCountry, geoChecked } = useAuth();

  // react-snap prerenders all four of these routes (see reactSnap.include), and
  // whatever the crawl renders becomes the static HTML that Google is served.
  // The crawl is headless and has no geo, so gating it would bake a redirect
  // into the snapshot and deindex the pages outright. The real decision belongs
  // in the visitor's browser.
  if (isPrerender()) return { allowed: true, resolvingGeo: false };

  // LOCAL DEV ONLY. The gate makes these pages unviewable from India, which
  // includes the machine they are built on, so there was no way to look at one
  // while working on it. Double guarded and cannot reach production: the
  // NODE_ENV branch is compiled out of the production bundle, and the hostname
  // test would fail on the live domain even if it were not.
  if (
    process.env.NODE_ENV === "development" &&
    typeof window !== "undefined" &&
    /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)
  ) {
    return { allowed: true, resolvingGeo: false };
  }

  // SIGNED IN: the guest geo probe never runs for them, so userCountry is still
  // sitting at its 'IN' default and means nothing. The account's own
  // currency_code is the reliable signal, and it is the same one BottomNav uses
  // to decide whether to show the India-only remedies tab.
  if (user) {
    return { allowed: currency !== null && currency !== "INR", resolvingGeo: currency === null };
  }

  // GUEST: the geoapify probe, the same signal the login sheet uses to decide
  // whether to offer Google and that useGuestChat uses to open the chat teaser.
  // A failed probe settles as India (geoChecked true, userCountry still 'IN'),
  // which sends the visitor home. That is the safe direction to fail: the
  // homepage works for everyone, a UAE pitch does not.
  return {
    allowed: !!userCountry && userCountry.toUpperCase() !== "IN",
    resolvingGeo: !geoChecked,
  };
}
